/**
 * AppICEMAP — ICEPrivacyWarning Dialog
 * Modal dialog shown when an ICE-category photo is about to be uploaded.
 *
 * Tag: <ice-privacy-warning>
 *
 * Attributes:
 *   open — boolean, whether dialog is visible
 *
 * Dispatches:
 *   ice-privacy:accept  → { }
 *   ice-privacy:cancel  → { }
 */

export class ICEPrivacyWarning extends HTMLElement {
	constructor() {
		super();
		this._open = false;
	}

	connectedCallback() {
		this._render();
		this._bindEvents();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	get open() {
		return this._open;
	}

	set open(val) {
		this._open = !!val;
		this._updateVisibility();
	}

	show() {
		this.open = true;
	}

	hide() {
		this.open = false;
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `
      <div class="dialog-backdrop" role="dialog" aria-modal="true"
           aria-labelledby="ice-warning-title">
        <div class="dialog dialog--warning">
          <i class="ph ph-shield-warning dialog__icon" aria-hidden="true"></i>
          <h3 id="ice-warning-title" class="dialog__title">Advertencia de privacidad</h3>
          <p class="dialog__body">
            Las fotos pueden contener rostros, placas o metadatos de ubicación.
            El sistema eliminará los metadatos automáticamente, pero revisa tu foto
            antes de publicar.
          </p>
          <p class="dialog__body">¿Deseas continuar?</p>
          <div class="dialog__actions">
            <button class="btn btn--ghost" data-action="cancel">Cancelar</button>
            <button class="btn btn--primary" data-action="accept">Entendido, continuar</button>
          </div>
        </div>
      </div>
    `;

		this._backdrop = this.querySelector(".dialog-backdrop");
	}

	/* ── Events ────────────────────────────────────────────────────────────── */

	_bindEvents() {
		if (!this._backdrop) return;

		// Close on backdrop click
		this._backdrop.addEventListener("click", (e) => {
			if (e.target === this._backdrop) {
				this._cancel();
			}
		});

		// Action buttons
		const acceptBtn = this.querySelector('[data-action="accept"]');
		const cancelBtn = this.querySelector('[data-action="cancel"]');

		if (acceptBtn) {
			acceptBtn.addEventListener("click", () => this._accept());
		}
		if (cancelBtn) {
			cancelBtn.addEventListener("click", () => this._cancel());
		}

		// Escape key
		this.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && this._open) {
				this._cancel();
			}
		});
	}

	_accept() {
		this.open = false;
		this.dispatchEvent(
			new CustomEvent("ice-privacy:accept", { bubbles: true }),
		);
	}

	_cancel() {
		this.open = false;
		this.dispatchEvent(
			new CustomEvent("ice-privacy:cancel", { bubbles: true }),
		);
	}

	_updateVisibility() {
		if (!this._backdrop) return;

		if (this._open) {
			this._backdrop.classList.add("dialog-backdrop--open");
			document.body.style.overflow = "hidden";

			// Focus trap: focus the first button
			const firstBtn = this.querySelector(".dialog__actions .btn");
			if (firstBtn) setTimeout(() => firstBtn.focus(), 100);
		} else {
			this._backdrop.classList.remove("dialog-backdrop--open");
			document.body.style.overflow = "";
		}
	}
}

customElements.define("ice-privacy-warning", ICEPrivacyWarning);
